import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Pill from '../components/ui/Pill';
import { EmptyState, SkeletonRows, ErrorNotice } from '../components/ui/States';
import { fmt, dateShort } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';
import {
  Participant,
  PARTICIPANT_STATUSES,
  PARTICIPANT_TONE,
  label,
  personName,
  errorMessage,
  inputClass,
  selectClass,
} from '../lib/programs';
import type {
  DataResponse,
  ProgramAddParticipantRequest,
  ProgramCycleList,
  ProgramCycleSummary,
  ProgramParticipant,
  ProgramParticipantPage,
  ProgramParticipantsQuery,
  ProgramStatusChangeRequest,
  ProgramSummary,
} from '../contracts';

type Cycle = ProgramCycleSummary;

const SOURCE_LABEL: Record<string, string> = {
  operator: 'Added by staff',
  group: 'From the cohort group',
  review: 'Fellow review',
  automatic: 'Automatic',
};

function AddParticipant({ cycle, onClose, onSaved }: { cycle: Cycle; onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('active');
  const [role, setRole] = useState('');
  const [note, setNote] = useState('');
  const save = useMutation(
    async () =>
      api.post<DataResponse<ProgramParticipant>>(`/programs/cycles/${cycle.id}/participants`, {
        email,
        status: status as ProgramAddParticipantRequest['status'],
        role: role || undefined,
        note: note || undefined,
      } satisfies ProgramAddParticipantRequest),
    { onSuccess: onSaved },
  );
  return (
    <Modal
      title={`Add to ${cycle.label}`}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!email.trim() || save.isLoading} onClick={() => save.mutate()}>
            {save.isLoading ? 'Adding…' : 'Add'}
          </Button>
        </div>
      }
    >
      <label className="block text-small text-ink-2 mb-1">Email of a member or a contact</label>
      <input className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} />
      <p className="text-small text-ink-2 mt-1">
        Someone who is not a member must be added on the{' '}
        <Link className="underline" to="/contacts">
          Contacts
        </Link>{' '}
        page first.
      </p>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <label className="block text-small text-ink-2 mb-1">Status</label>
          <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
            {PARTICIPANT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {label(s)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-small text-ink-2 mb-1">Role (optional)</label>
          <input
            className={inputClass}
            placeholder="fellow, member, chair…"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          />
        </div>
      </div>
      <label className="block text-small text-ink-2 mt-4 mb-1">Note (optional)</label>
      <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not add the participant.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function ParticipantDetail({
  participant,
  mutable,
  onClose,
  onSaved,
}: {
  participant: Participant;
  mutable: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState(participant.status);
  const [note, setNote] = useState('');
  const save = useMutation(
    async () =>
      api.post<DataResponse<ProgramParticipant>>(`/programs/participants/${participant.id}/status`, {
        status: status as ProgramStatusChangeRequest['status'],
        note: note || undefined,
      } satisfies ProgramStatusChangeRequest),
    { onSuccess: onSaved },
  );
  const detached = participant.person.type === 'detached';
  return (
    <Modal
      title={personName(participant.person)}
      onClose={onClose}
      footer={
        mutable && !detached ? (
          <div className="flex justify-end gap-3 w-full">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              disabled={status === participant.status || save.isLoading}
              onClick={() => save.mutate()}
            >
              {save.isLoading ? 'Saving…' : 'Change status'}
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className="text-table space-y-1">
        <div>
          {label(participant.role)} · {SOURCE_LABEL[participant.source] ?? participant.source}
        </div>
        {participant.person.email ? <div className="text-ink-2">{participant.person.email}</div> : null}
        {participant.person.type === 'member' && participant.person.id ? (
          <Link className="underline text-small" to={`/analytics/members/${participant.person.id}`}>
            Member 360
          </Link>
        ) : null}
        {detached ? (
          <p className="text-ink-2 text-small">The member record was deleted; this row keeps the program history.</p>
        ) : null}
      </div>
      {mutable && !detached ? (
        <div className="grid grid-cols-2 gap-4 mt-5">
          <div>
            <label className="block text-small text-ink-2 mb-1">Status</label>
            <select className={inputClass} value={status} onChange={(e) => setStatus(e.target.value)}>
              {PARTICIPANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {label(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-small text-ink-2 mb-1">Note (optional)</label>
            <input className={inputClass} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
      ) : null}
      {save.error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(save.error, 'Could not change the status.')}</ErrorNotice>
        </div>
      ) : null}
      <div className="text-small text-ink-2 mt-6 mb-2">History</div>
      <ul className="text-small space-y-1">
        {participant.history.map((h, i) => (
          <li key={i} className="border-b border-line pb-1">
            {dateShort(h.at)}: {h.from ? `${label(h.from)} → ` : ''}
            {label(h.to)}
            {h.source && h.source !== 'operator' ? (
              <span className="text-ink-2"> ({SOURCE_LABEL[h.source] ?? h.source})</span>
            ) : null}
            {h.note ? <div className="text-ink-2">{h.note}</div> : null}
          </li>
        ))}
      </ul>
    </Modal>
  );
}

export default function Programs() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const queryClient = useQueryClient();
  const [programKey, setProgramKey] = useState<string | null>(null);
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<Participant | null>(null);

  const programs = useQuery<ProgramSummary[]>(
    'programs',
    async () => (await api.get<DataResponse<ProgramSummary[]>>('/programs')).data.data,
  );
  useEffect(() => {
    if (!programKey && programs.data?.length) setProgramKey(programs.data[0].key);
  }, [programs.data, programKey]);

  const cycles = useQuery<ProgramCycleList>(
    ['program-cycles', programKey],
    async () => (await api.get<DataResponse<ProgramCycleList>>(`/programs/${programKey}/cycles`)).data.data,
    {
      enabled: Boolean(programKey),
    },
  );
  useEffect(() => {
    if (cycles.data && !cycles.data.cycles.some((c) => c.id === cycleId)) setCycleId(cycles.data.cycles[0]?.id ?? null);
  }, [cycles.data, cycleId]);
  const cycle = cycles.data?.cycles.find((c) => c.id === cycleId) ?? null;

  const participants = useQuery<ProgramParticipantPage>(
    ['participants', cycleId, status, q, page],
    async () =>
      (
        await api.get<DataResponse<ProgramParticipantPage>>('/programs/participants', {
          params: {
            cycleId: cycleId ?? undefined,
            status: status || undefined,
            q: q || undefined,
            page: String(page),
          } satisfies ProgramParticipantsQuery,
        })
      ).data.data,
    { enabled: Boolean(cycleId), keepPreviousData: true },
  );
  const refresh = () => {
    queryClient.invalidateQueries('participants');
    queryClient.invalidateQueries('program-cycles');
    queryClient.invalidateQueries('programs');
  };
  const pages = participants.data ? Math.max(1, Math.ceil(participants.data.total / participants.data.limit)) : 1;

  return (
    <>
      <PageHeader
        title="Programs"
        sub="Programs DIVERT runs, their cycles (a Fellows cohort year, a committee term) and who is in each. Groups stay the place for group membership; a participant here carries the person’s status in that cycle and its history."
      />
      {programs.error ? <ErrorNotice>Could not load programs.</ErrorNotice> : null}
      <div className="flex flex-wrap gap-2 mb-4">
        {programs.data?.map((p) => (
          <Button
            key={p.id}
            size="sm"
            variant={p.key === programKey ? 'dark' : 'outline'}
            onClick={() => {
              setProgramKey(p.key);
              setCycleId(null);
              setPage(1);
            }}
          >
            {`${p.name} · ${fmt(p.participantCount)}`}
          </Button>
        ))}
      </div>
      {cycles.data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {cycles.data.cycles.map((c) => {
            const total = Object.values(c.byStatus).reduce((a, b) => a + b, 0);
            return (
              <button
                key={c.id}
                onClick={() => {
                  setCycleId(c.id);
                  setPage(1);
                }}
                className={`text-left bg-white border px-4 py-3 ${c.id === cycleId ? 'border-line-strong border-2' : 'border-line'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-[18px]">{c.label}</span>
                  <Pill tone={c.state === 'active' ? 'active' : c.state === 'closed' ? 'inactive' : 'planned'}>
                    {label(c.state)}
                  </Pill>
                </div>
                <div className="text-small text-ink-2 mt-1">
                  {fmt(total)} participant{total === 1 ? '' : 's'}
                  {Object.entries(c.byStatus).length
                    ? ` · ${Object.entries(c.byStatus)
                        .map(([s, n]) => `${n} ${s}`)
                        .join(', ')}`
                    : ''}
                </div>
              </button>
            );
          })}
          {!cycles.data.cycles.length ? (
            <EmptyState title="No cycles yet">
              Cycles are created by migrate (cohort years) and the backfill script.
            </EmptyState>
          ) : null}
        </div>
      ) : null}
      {cycle ? (
        <Panel>
          <PanelHeader
            title={`${cycles.data?.program.name} ${cycle.label}`}
            note={participants.data ? fmt(participants.data.total) : undefined}
          >
            <select
              className={selectClass}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Every status</option>
              {PARTICIPANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {label(s)}
                </option>
              ))}
            </select>
            {mutable ? (
              <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
                Add participant
              </Button>
            ) : null}
          </PanelHeader>
          <PanelBody>
            <input
              className={`${inputClass} mb-3`}
              placeholder="Search by name or email"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
            {participants.isLoading ? (
              <SkeletonRows rows={6} />
            ) : participants.error ? (
              <ErrorNotice>{errorMessage(participants.error, 'Could not load participants.')}</ErrorNotice>
            ) : !participants.data?.participants.length ? (
              <EmptyState title="Nobody here yet">
                {q || status ? 'Nobody matches.' : 'Add a participant, or run the backfill for cohort groups.'}
              </EmptyState>
            ) : (
              <>
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-small text-ink-2 border-b border-line-strong">
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Role</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Since</th>
                      <th className="py-2 pr-4">Source</th>
                      <th className="py-2 pr-4" />
                    </tr>
                  </thead>
                  <tbody>
                    {participants.data.participants.map((p) => (
                      <tr key={p.id} className="border-b border-line text-table align-top">
                        <td className="py-[9px] pr-4">
                          <div>{personName(p.person)}</div>
                          <div className="text-small text-ink-2">
                            {p.person.type === 'contact' ? 'Contact' : p.person.email}
                          </div>
                        </td>
                        <td className="py-[9px] pr-4">{label(p.role)}</td>
                        <td className="py-[9px] pr-4">
                          <Pill tone={PARTICIPANT_TONE[p.status] ?? 'planned'}>{label(p.status)}</Pill>
                        </td>
                        <td className="py-[9px] pr-4 text-ink-2">{dateShort(p.statusChangedAt)}</td>
                        <td className="py-[9px] pr-4 text-ink-2">{SOURCE_LABEL[p.source] ?? p.source}</td>
                        <td className="py-[9px] pr-4 text-right">
                          <Button variant="ghost" size="sm" onClick={() => setOpen(p)}>
                            {mutable ? 'Status' : 'History'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pages > 1 ? (
                  <div className="flex items-center gap-3 mt-4 text-small text-ink-2">
                    <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                      Previous
                    </Button>
                    Page {page} of {pages}
                    <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                      Next
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </PanelBody>
        </Panel>
      ) : null}
      {adding && cycle ? (
        <AddParticipant
          cycle={cycle}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            refresh();
          }}
        />
      ) : null}
      {open ? (
        <ParticipantDetail
          participant={open}
          mutable={mutable}
          onClose={() => setOpen(null)}
          onSaved={() => {
            setOpen(null);
            refresh();
          }}
        />
      ) : null}
    </>
  );
}
